import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Sparkles, Loader2, ExternalLink, ShieldAlert, TrendingUp, Lightbulb, Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { showCreditReceipt } from "../../lib/creditReceipt";
import QuoteRequestDialog from "./QuoteRequestDialog";

const TYPE_LABELS = {
  mayorista: "Mayorista",
  supermercado: "Supermercado",
  especialista: "Especialista",
  distribuidor: "Distribuidor",
  otros: "Otros",
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

/**
 * Diálogo que pide a la IA 2-3 proveedores alternativos para un proveedor
 * con scorecard bajo (grade C/D). Coste: 2 créditos por consulta.
 *
 * Props:
 *   open, onOpenChange
 *   supplier: {supplier_id, name, category_hint}
 *   restaurantId
 *   onCreate?: (alt) => Promise — opcional, si el usuario quiere añadirlo a su lista
 */
export default function AlternativeSuppliersDialog({
  open, onOpenChange, supplier, restaurantId, onCreate,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState({}); // {idx: bool}
  const [quoteAlt, setQuoteAlt] = useState(null);

  const fetchAlternatives = async () => {
    if (!supplier?.supplier_id || !restaurantId) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/suppliers/${supplier.supplier_id}/ai-alternatives`,
        { method: "POST" },
      );
      setData(res);
      if (res._credits_receipt) showCreditReceipt(res._credits_receipt);
    } catch (e) {
      setError(e?.message || "Error consultando la IA");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (alt, idx) => {
    if (!onCreate) return;
    setCreating((s) => ({ ...s, [idx]: true }));
    try {
      await onCreate({
        name: alt.name,
        category_hint: alt.type,
        notes: `[Sugerido por IA — alternativa a ${supplier?.name}] ${alt.why_better}`,
      });
      toast.success(`${alt.name} añadido a tus proveedores`);
    } catch (e) {
      toast.error(e?.message || "Error al añadir");
    } finally {
      setCreating((s) => ({ ...s, [idx]: false }));
    }
  };

  const handleOpenChange = (o) => {
    if (!loading) {
      if (!o) {
        setData(null);
        setError(null);
      }
      onOpenChange(o);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="alt-suppliers-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Alternativas con IA
          </DialogTitle>
          <DialogDescription>
            Sugerencias para reemplazar a <strong>{supplier?.name}</strong>, basadas en su perfil
            de fallos y el contexto de tu restaurante. Coste: 2 créditos.
          </DialogDescription>
        </DialogHeader>

        {!data && !loading && !error && (
          <div className="py-8 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center">
              <Lightbulb className="w-7 h-7 text-violet-600" />
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              La IA analizará el scorecard de los últimos 90 días, los ingredientes que más fallan
              y la categoría de este proveedor para sugerirte 2-3 alternativas reales.
            </p>
            <Button
              onClick={fetchAlternatives}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="alt-suppliers-fetch-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Buscar alternativas (2 cr)
            </Button>
          </div>
        )}

        {loading && (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-violet-600" />
            <p className="text-sm text-muted-foreground">
              Consultando IA y buscando proveedores reales para {supplier?.category_hint || "esta categoría"}...
            </p>
          </div>
        )}

        {error && (
          <div className="py-8 text-center">
            <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p className="text-sm text-red-700 font-medium mb-1">No se pudieron obtener alternativas</p>
            <p className="text-xs text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={fetchAlternatives} data-testid="alt-suppliers-retry-btn">
              Reintentar
            </Button>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Context summary */}
            {data.context && (
              <div
                className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs space-y-1"
                data-testid="alt-suppliers-context"
              >
                <p className="font-semibold text-red-900">
                  Perfil de {data.context.supplier_name}: nota {data.context.grade} ({data.context.score}/100)
                </p>
                <p className="text-red-700">
                  {Math.round((data.context.incident_rate || 0) * 100)}% incidencias ·{" "}
                  {Math.round((data.context.completeness_rate || 0) * 100)}% completos ·{" "}
                  ~{formatCurrency(data.context.monthly_spend)}/mes de gasto
                </p>
                {data.context.top_failing_ingredients?.length > 0 && (
                  <p className="text-red-700">
                    Ingredientes problemáticos: {data.context.top_failing_ingredients.slice(0, 5).join(", ")}
                  </p>
                )}
              </div>
            )}

            {/* Estimated savings highlight */}
            {data.estimated_savings_label && (
              <div
                className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-3"
                data-testid="alt-suppliers-savings"
              >
                <TrendingUp className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-900">{data.estimated_savings_label}</p>
              </div>
            )}

            {/* Alternatives list */}
            <div className="space-y-2">
              {data.alternatives.map((alt, idx) => (
                <div
                  key={idx}
                  className="border rounded-lg p-3 hover:border-violet-300 transition-colors"
                  data-testid={`alt-supplier-row-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{alt.name}</p>
                        <Badge variant="outline" className="text-[10px]">
                          {TYPE_LABELS[alt.type] || alt.type}
                        </Badge>
                        {alt.fit_score >= 8 && (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                            Encaje {alt.fit_score}/10
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{alt.why_better}</p>
                      {alt.contact_hint && (
                        <p className="text-xs text-violet-700 mt-1 inline-flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          {alt.contact_hint}
                        </p>
                      )}
                    </div>
                    {onCreate && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCreate(alt, idx)}
                          disabled={creating[idx]}
                          data-testid={`alt-supplier-add-btn-${idx}`}
                        >
                          {creating[idx] ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <Plus className="w-3.5 h-3.5 mr-1" /> Añadir
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setQuoteAlt(alt)}
                          className="border-violet-300 text-violet-700 hover:bg-violet-50"
                          data-testid={`alt-supplier-quote-btn-${idx}`}
                          title="Pedir presupuesto por email (IA, 2 cr)"
                        >
                          <Mail className="w-3.5 h-3.5 mr-1" /> Presupuesto
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Switch recommendation */}
            {data.switch_recommendation && (
              <div
                className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm"
                data-testid="alt-suppliers-recommendation"
              >
                <p className="font-medium text-violet-900 mb-0.5 flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5" /> Recomendación
                </p>
                <p className="text-violet-800 text-xs">{data.switch_recommendation}</p>
              </div>
            )}

            {data._provider && (
              <p className="text-[10px] text-muted-foreground text-right">
                Generado por {data._provider}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
            data-testid="alt-suppliers-close-btn"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>

      <QuoteRequestDialog
        open={!!quoteAlt}
        onOpenChange={(o) => { if (!o) setQuoteAlt(null); }}
        alternative={quoteAlt}
        badSupplierId={supplier?.supplier_id}
        restaurantId={restaurantId}
      />
    </Dialog>
  );
}
